class ShelfStickyBar extends HTMLElement {
	constructor() {
		super();
		this.onAddToCartClick = this.handleAddToCart.bind(this);
	}

	connectedCallback() {
		this.classList.add('shelf__sticky-bar');
		this.collapseButton = this.querySelector('.shelf-collapse__btn');
		this.initializeElements();
		this.observeFooterIntersection();
		this.variants = this.loadConfiguratorVariants();
		this.discounts = this.loadConfiguratorDiscounts();
		if (!this.variants.length) {
			return;
		}
		this.bindEventListeners();
	}

	disconnectedCallback() {
		this.unbindEventListeners();
	}

	bindEventListeners() {
		this.addToCartButton?.addEventListener('click', this.onAddToCartClick);
		this.collapseButton?.addEventListener('click', () => {
			this.classList.toggle('collapsed');
			this.collapseButton.classList.toggle('collapsed');
		});
	}

	unbindEventListeners() {
		this.addToCartButton?.removeEventListener('click', this.onAddToCartClick);
	}
	hide() {
		this.classList.add('hide');
	}
	show() {
		this.classList.remove('hide');
	}
	initializeElements() {
		this.rawDataEl = document.getElementById('configurator-product-data');
		this.editButton = this.querySelector('.edit-shelf-items');
		this.listEl = this.querySelector('.items-list');
		this.subtotalEl = this.querySelector('[shelf-output-price]');
		this.subtotalDiscountedEl = this.querySelector('[shelf-output-price-discounted]');
		this.discountedAmountEl = this.querySelector('[shelf-output-discounted-price]');
		this.discountCodeEl = this.querySelector('[shelf-output-discount-code]');
		this.discountAmountWrapperEl = this.querySelector('[shelf-output-discount-amount-wrapper]');
		this.addToCartButton = this.querySelector('.add-to-cart');
		this.footerEl = document.querySelector('.site-footer');
	}

	loadConfiguratorVariants() {
		if (!this.rawDataEl) {
			console.log('Missing #configurator-product-data');
			return [];
		}

		try {
			return JSON.parse(this.rawDataEl.textContent);
		} catch (err) {
			console.log('Invalid JSON in configurator-product-data', err);
			return [];
		}
	}
	loadConfiguratorDiscounts() {
		try {
			return JSON.parse(this.dataset.configuratorDiscounts || '[]');
		} catch (err) {
			console.error('Invalid JSON in configurator-discounts', err);
			return [];
		}
	}

	initializeLineItems(lineItemsInShelf, isShelfFull) {
		this.items = lineItemsInShelf
			.map((item) => {
				const match = this.variants?.find((v) => v.variantID === item.variantID);
				return match ? { ...item, ...match } : null;
			})
			.filter(Boolean);
		this.renderBar(isShelfFull);
	}
	getAppliedDiscounts(subtotalPrice) {
		return this.discounts
			.filter((discount) => discount.min_threshold <= subtotalPrice)
			.sort((a, b) => a.discount_percent - b.discount_percent);
	}
	getNextDiscounts(subtotalPrice) {
		return this.discounts
			.filter((discount) => discount.min_threshold > subtotalPrice)
			.sort((a, b) => b.discount_percent - a.discount_percent);
	}
	calculateDiscountedPrice(subtotalPrice) {
		const appliedDiscount = this.getAppliedDiscounts(subtotalPrice).pop();
		if (appliedDiscount) {
			const discountPercent = appliedDiscount.discount_percent / 100.0;
			const discountAmount = this.items.reduce((total, item) => {
				const discountPercent = appliedDiscount.discount_percent / 100.0;
				const discountAmount = item.count * Math.floor(item.productPrice * discountPercent);
				return total + discountAmount;
			}, 0);
			return subtotalPrice - discountAmount;
		}
		return subtotalPrice;
	}
	updateDiscountBar(subtotalPrice, isShelfFull) {
		const appliedDiscount = this.getAppliedDiscounts(subtotalPrice).pop();
		const nextDiscount = isShelfFull ? null : this.getNextDiscounts(subtotalPrice).pop();
		this.dispatchEvent(
			new CustomEvent('discount-bar:update', {
				detail: {
					subtotalPrice,
					appliedDiscount: {
						discountPercent: appliedDiscount?.discount_percent,
						threshold: appliedDiscount?.min_threshold,
					},
					nextDiscount: {
						discountPercent: nextDiscount?.discount_percent,
						threshold: nextDiscount?.min_threshold,
					},
				},
			}),
		);
	}
	renderBar(isShelfFull) {
		if (!this.items.length) {
			this.classList.remove('visible');
			return;
		}
		this.classList.add('visible');
		this.listEl.innerHTML = '';

		const subtotalPrice = this.items.reduce((total, item) => total + item.productPrice * item.count, 0);
		const appliedDiscount = this.getAppliedDiscounts(subtotalPrice).pop();
		this.items.forEach((item) => {
			const el = this.createBarItemElement(item, appliedDiscount);
			this.listEl.appendChild(el);
		});
		const discountedPrice = this.calculateDiscountedPrice(subtotalPrice);
		this.subtotalEl.innerHTML = theme.Currency.formatMoney(subtotalPrice);
		this.subtotalEl.classList.toggle('shelf-output__price--strikethrough', discountedPrice < subtotalPrice);
		this.subtotalDiscountedEl.innerHTML = theme.Currency.formatMoney(discountedPrice);
		this.subtotalDiscountedEl.classList.toggle('hide', discountedPrice >= subtotalPrice);
		this.discountedAmountEl.innerHTML = `(-${theme.Currency.formatMoney(subtotalPrice - discountedPrice)})`;
		this.discountCodeEl.innerHTML = appliedDiscount?.discount_code || '';
		this.discountAmountWrapperEl?.classList.toggle('hide', discountedPrice >= subtotalPrice);
		this.updateDiscountBar(subtotalPrice, isShelfFull);
	}

	handleAddToCart() {
		const payload = this.items.map((item) => ({
			id: parseInt(item.variantID),
			quantity: item.count,
		}));

		this.addMultipleToCart(payload);
	}

	createBarItemElement(item, appliedDiscount) {
		const el = document.createElement('a');
		el.href = item.variantUrl;
		const discountPercent = appliedDiscount ? appliedDiscount.discount_percent / 100.0 : 0;
		const discountAmount = appliedDiscount ? Math.floor(item.productPrice * discountPercent) : 0;
		const discountText = appliedDiscount
			? ` <span class="shelf__bar-price shelf__bar-price--discounted">${theme.Currency.formatMoney(item.productPrice - discountAmount)}</span>`
			: '';
		el.className = 'item';
		el.innerHTML = `
        <img class="shelf__bar-img" src="${item.productImage}" alt="${item.productTitle}" width="100" height="100" loading="lazy" />
        <span class="shelf__bar-title">${item.productTitle}</span>
				<div class="shelf__bar-price-wrapper">
					${discountText}
					<span class="shelf__bar-price ${appliedDiscount && 'shelf__bar-price--strikethrough'}">${theme.Currency.formatMoney(item.productPrice)}</span>
				</div>
        <span class="shelf__bar-quantity">${item.count}</span>
      `;
		return el;
	}

	observeFooterIntersection() {
		const thresholds = [];
		for (let i = 0; i <= 10; i++) {
			thresholds.push(i / 10);
		}

		const observerOptions = {
			rootMargin: '0px',
			threshold: 0.1,
		};

		this.footerObserver = new IntersectionObserver((entries) => {
			const hasItems = Array.isArray(this.items) && this.items.length > 0;
			entries.forEach((entry) => {
				if (!hasItems) {
					this.classList.remove('visible');
					return;
				}

				if (entry.isIntersecting) {
					this.classList.remove('visible');
				} else {
					this.classList.add('visible');
				}
			});
		}, observerOptions);

		if (this.footerEl) {
			this.footerObserver.observe(this.footerEl);
		}
	}

	addMultipleToCart(items) {
		this.addToCartButton.classList.add('btn--loading');

		fetch('/cart/add.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			body: JSON.stringify({ items }),
		})
			.then((res) => res.json())
			.then((data) => {
				this.addToCartButton.classList.remove('btn--loading');

				// trigger add to cart event (AjaxProduct behavior)
				document.dispatchEvent(
					new CustomEvent('ajaxProduct:added', {
						detail: {
							product: data,
							addToCartBtn: this.addToCartButton,
						},
					}),
				);
			})

			.catch((err) => {
				console.error('Cart error:', err);
				this.addToCartButton.classList.remove('btn--loading');
				document.dispatchEvent(
					new CustomEvent('ajaxProduct:error', {
						detail: {
							errorMessage: err.message,
						},
					}),
				);
			});
	}
}

customElements.define('shelf-sticky-bar', ShelfStickyBar);
